import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_LIKEABILITY,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitNameKeyMap,
} from "../../utils/general-config";
import {
  getCookie,
  getEnneagramBannerImage,
  likeabilityTraitColors,
} from "../../utils/utility-functions";
import Header from "../../components/header";
import BackToTopButton from "../../components/back-to-top/component";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const LikeabilityReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const [contentData, setContentData] = useState({});
  const navigate = useNavigate();
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  // const token = getCookie("jwtToken");
  const { userGUID, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  // Function to find the score of a specific trait by traitId
  const findScoreByTraitId = (traitId) => {
    const trait = userTraits?.find((item) => item.traitId === traitId);
    return trait ? trait?.score : null; // Return the score or null if not found
  };

  const likeabilityTraitId = {
    empathy: "3adaccd3-c4c9-48ca-a1dd-19c411798e85",
    social: "aebc80d7-a2a2-4ea9-983d-90798c6a38fe",
    approachability: "00e19526-acc8-43d5-bb6c-bdc4f53a5a2a",
    trustworthiness: "db65c632-f5a9-43ed-8a85-cf494f1aa45c",
  };

  const totalScore = userTraits?.reduce((accumulator, trait) => {
    return accumulator + trait.score;
  }, 0);
  const empathyScore = findScoreByTraitId(likeabilityTraitId.empathy);
  const socialScore = findScoreByTraitId(likeabilityTraitId.social);
  const approachabilityScore = findScoreByTraitId(
    likeabilityTraitId.approachability
  );
  const trustworthinessScore = findScoreByTraitId(
    likeabilityTraitId.trustworthiness
  );

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  useEffect(() => {
    fetchLikeabilityData();
  }, []);

  const fetchLikeabilityData = async () => {
    setIsLoading(true); // Start loading
    try {
      // Fetch user traits
      const traitsResponse =
        await getAssessmentResultByAssessmentAttemptIdConnect(
          assessmentAttemptId,
          currentLocale
        );
      if (traitsResponse) {
        setUserTraits(traitsResponse.userTraits);
        setCurrentAssessmentAttemptData(traitsResponse);
      }

      // Fetch content
      const contentResponse = await getInsightfulContentConnect({
        modelType:
          MODEL_TYPE_LIKEABILITY[currentLocale] ||
          MODEL_TYPE_LIKEABILITY[LOCALE.EN],
        contentKey: CONTENT_KEY_BIG_FIVE,
      });
      if (contentResponse) {
        const { content } = contentResponse;

        setContentData(content);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const bannerImage = getEnneagramBannerImage();

  // const replaceScorePlaceholder = (text, score) =>
  //   text?.replace(/{score}|{score_range}/g, score) || "";

  const replaceScorePlaceholder = (text, score) => {
    if (!text) return "";
    return text.replace(/\{(Score|score|score_range)\}/g, String(score));
  };

  const getScoreCategory = (score) => {
    if (score >= 151) return { category: "High", index: 0 };
    else if (score >= 101) return { category: "Moderate", index: 1 };
    else return { category: "Low", index: 2 };
  };

  const getSectionCategory = (score) => {
    if (score >= 38) return { category: "High", index: 0 };
    else if (score >= 25) return { category: "Moderate", index: 1 };
    else return { category: "Low", index: 2 };
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        TAKE TEST
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const renderSection = (sectionData, color, selectedIndex, scoreValue) => {
    if (!sectionData) return null;
    const rootHeading = replaceScorePlaceholder(
      sectionData.RootHeading,
      scoreValue
    );

    const scoreInsertion = sectionData.ScoreInsertion
      ? replaceScorePlaceholder(sectionData.ScoreInsertion, scoreValue)
      : "";

    let contentHtml = null;
    if (Array.isArray(sectionData.Content)) {
      const selectedContent = sectionData.Content[selectedIndex] || {};
      contentHtml = (
        <div
          className="p-6 rounded-lg shadow-md mb-4"
          style={{ backgroundColor: color }}
        >
          <h4 className="text-xl font-semibold mb-2">
            {selectedContent.Heading || ""}
          </h4>
          <p className="text-gray-700">{selectedContent.Content || ""}</p>
        </div>
      );
    } else {
      contentHtml = (
        <div
          className="p-6 rounded-lg shadow-md mb-4"
          style={{ backgroundColor: color }}
        >
          <p className="text-gray-700">{sectionData.Content}</p>
        </div>
      );
    }

    return (
      <section className="mb-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">{rootHeading}</h3>
        {scoreInsertion && (
          <p className="mb-4 text-lg text-gray-600">{scoreInsertion}</p>
        )}
        {contentHtml}
      </section>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const ProgressCircle = ({
    color = "#3B82F6",
    duration = 2000,
    targetPercentage = 50,
  }) => {
    const [percentage, setPercentage] = useState(0);
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const intervalTime = 30; // Time in milliseconds for each update

    useEffect(() => {
      const steps = duration / intervalTime; // Number of updates
      const stepSize = targetPercentage / steps; // Percentage to increase per step

      let currentPercentage = 0;
      const interval = setInterval(() => {
        currentPercentage += stepSize;
        if (currentPercentage >= targetPercentage) {
          currentPercentage = targetPercentage;
          clearInterval(interval);
        }
        setPercentage(parseInt(currentPercentage)); // Ensure whole numbers
      }, intervalTime);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }, [duration, targetPercentage]);

    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%] ease-in duration-100">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Animated Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: "stroke-dashoffset 0.1s ease-in-out",
              }}
            />
          </svg>

          {/* Percentage Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-2xl font-bold text-gray-700">
            <span>{targetPercentage}%</span>
          </div>
        </div>
      </div>
    );
  };

  const progressBarData = userTraits?.map(({ trait, percentage }) => {
    const localeKey =
      traitNameKeyMap[currentLocale] || traitNameKeyMap[LOCALE.EN];
    return {
      label: trait[localeKey],
      percentage,
      color: likeabilityTraitColors[trait.title] || "rgba(0, 0, 0, 1)",
    };
  });

  const CallToActionSection = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg shadow-lg">
        <h2 className="text-red-800 text-xl font-bold mb-2">
          {t("error.error_message")}
        </h2>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchLikeabilityData}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          {t("error.try_again")}
        </button>
      </div>
    );
  }

  if (!contentData) return null;

  const overallTier = getScoreCategory(totalScore);
  const socialTier = getSectionCategory(socialScore);
  const empathyTier = getSectionCategory(empathyScore);
  const approachTier = getSectionCategory(approachabilityScore);
  const trustTier = getSectionCategory(trustworthinessScore);

  let strengthArray = [];

  let areasOfImprovementArray = [];

  const strengthDescription = contentData?.strengthsDescriptions;

  const actionableTips = contentData?.actionableTips;

  function addPotentialAreasOfImprovement(trait, strengthKey) {
    if (trait === "Low" || trait === "Moderate") {
      areasOfImprovementArray.push(actionableTips?.Content[strengthKey]);
    }
  }

  function addStrengths(trait, strengthKey) {
    if (trait === "High") {
      strengthArray.push(strengthDescription?.Content[strengthKey]);
    }
  }

  // Social Skills
  if (socialTier?.category === "High") {
    addStrengths(socialTier?.category, "socialSkills");
  } else {
    addPotentialAreasOfImprovement(socialTier?.category, "socialSkills");
  }

  // Empathy
  if (empathyTier?.category === "High") {
    addStrengths(empathyTier?.category, "empathy");
  } else {
    addPotentialAreasOfImprovement(empathyTier?.category, "empathy");
  }

  // Trustworthiness
  if (trustTier?.category === "High") {
    addStrengths(trustTier?.category, "trustworthiness");
  } else {
    addPotentialAreasOfImprovement(trustTier?.category, "trustworthiness");
  }

  // Approachability
  if (approachTier?.category === "High") {
    addStrengths(approachTier?.category, "approachability");
  } else {
    addPotentialAreasOfImprovement(approachTier?.category, "approachability");
  }

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>
              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const InfoCard = ({ heading, items, bgClr }) => {
    return (
      <div
        className={`bg-${bgClr}-50 p-4 md:p-6 rounded-lg shadow-md border border-green-50 w-full h-full flex flex-col`}
      >
        <h2 className="font-bold text-lg text-gray-900">{heading}</h2>
        <ul className="list-disc list-outside pl-5 text-gray-700 mt-4">
          {items.map((item, index) => (
            <li key={index} className="break-words">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  {/* {renderHeroBanner(contentData.header, "blue")} */}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ">
                      <div>
                        <p className="text-lg text-gray-700">
                          {contentData?.intro}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-3xl font-bold mb-2 text-center">
                          {t("report.likeability.overall_result")}:
                        </h4>
                        <ProgressCircle targetPercentage={totalScore / 2} />
                      </div>
                      <div>
                        <ul className="space-y-4">
                          {progressBarData?.map((item, index) => (
                            <ProgressBar
                              key={index}
                              label={item.label}
                              percentage={item.percentage}
                              color={item.color}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 sm:p-8 shadow-md">
                    {renderSection(
                      contentData?.overallScore,
                      "#f0f6fe",
                      overallTier.index,
                      totalScore
                    )}
                    <h3 className="text-3xl font-bold text-gray-800 mb-4">
                      {t("report.likeability.section_scores")}
                    </h3>
                    {renderSection(
                      contentData?.socialSkills,
                      "#eef2fe",
                      socialTier.index,
                      socialScore
                    )}
                    {renderSection(
                      contentData?.empathy,
                      "#faf5fe",
                      empathyTier?.index,
                      empathyScore
                    )}
                    {renderSection(
                      contentData?.approachability,
                      "#f1fdfa",
                      approachTier.index,
                      approachabilityScore
                    )}
                    {renderSection(
                      contentData?.trustworthiness,
                      "#fef7ee",
                      trustTier.index,
                      trustworthinessScore
                    )}
                  </div>
                </ExpandableSection>
              </section>

              <section class="rounded-xl shadow-lg overflow-hidden mb-8 bg-white">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.likeability.additional_insights")}
                    />
                  }
                >
                  {strengthArray?.length > 0 &&
                    contentData?.strengths &&
                    contentData?.strengthsDescriptions && (
                      <>
                        <div class="my-8 rounded-xl overflow-hidden shadow-md mx-5">
                          <div class="bg-indigo-600 p-5 flex items-center">
                            <h3 class="text-xl font-bold text-white">
                              {contentData?.strengths?.RootHeading}
                            </h3>
                          </div>
                          <div class="bg-white p-6">
                            <p class="text-gray-700 whitespace-pre-line">
                              {contentData?.strengths?.Content}
                            </p>
                          </div>
                          <div class="mb-8">
                            <h3 class="text-2xl font-bold text-gray-800 mt-4 mx-8">
                              {contentData?.strengthsDescriptions?.RootHeading}
                            </h3>
                            <div className="grid grid-cols-1 gap-6 p-6">
                              {strengthArray?.map((item, index) => (
                                <div key={index} className="flex">
                                  <InfoCard
                                    items={item?.Content}
                                    heading={item?.Heading}
                                    bgClr={"green"}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                  {areasOfImprovementArray.length > 0 &&
                    contentData?.areasForImprovement &&
                    contentData?.actionableTips && (
                      <>
                        <div class="my-8 rounded-xl overflow-hidden shadow-md mx-5">
                          <div class="bg-indigo-600 p-5 flex items-center">
                            <h3 class="text-xl font-bold text-white">
                              {contentData?.areasForImprovement?.RootHeading}
                            </h3>
                          </div>
                          <div class="bg-white p-6">
                            <p class="text-gray-700 whitespace-pre-line">
                              {contentData?.areasForImprovement?.Content}
                            </p>
                          </div>
                          <div class="mb-8">
                            <h3 class="text-2xl font-bold text-gray-800 my-4 mx-8">
                              {contentData?.actionableTips?.RootHeading}
                            </h3>
                            <div className="grid grid-cols-1 gap-6 p-6">
                              {areasOfImprovementArray?.map((item, index) => (
                                <div key={index} className="flex">
                                  <InfoCard
                                    items={item?.Content}
                                    heading={item?.Heading}
                                    bgClr={"orange"}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                  {contentData.conclusion && (
                    <div class="mb-8 rounded-xl overflow-hidden shadow-md mx-5">
                      <div class="bg-indigo-600 p-5 flex items-center">
                        <div class="mr-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-8 w-8 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h3 class="text-xl font-bold text-white">
                          {t("report.likeability.conclusion")}
                        </h3>
                      </div>
                      <div class="bg-white p-6">
                        <p class="text-gray-700 whitespace-pre-line">
                          {contentData.conclusion}
                        </p>
                      </div>
                    </div>
                  )}
                </ExpandableSection>
              </section>
              {CallToActionSection({ contentData })}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default LikeabilityReport;
